import { useMemo, useState } from 'react';
import { Button, Field, Input, Modal } from '../../../components/ui';
import { toDateOnly } from '../../../lib/period';

/**
 * Data e hora do envio agendado.
 *
 * O instante e montado como hora LOCAL (`YYYY-MM-DDTHH:MM:00` sem fuso) e so
 * depois vira ISO: `new Date('2026-09-25')` seria meia-noite UTC e agendaria
 * para as 21h do dia anterior em Sao Paulo. Padrao: amanha as 9h, inicio da
 * janela comercial.
 */
export function ModalAgendar({
  open,
  onClose,
  onConfirm,
  janelaInicio,
  janelaFim,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (iso: string) => void;
  readonly janelaInicio: string;
  readonly janelaFim: string;
}): JSX.Element | null {
  const amanha = useMemo(() => toDateOnly(new Date(Date.now() + 86_400_000)), []);
  const [data, setData] = useState(amanha);
  const [hora, setHora] = useState('09:00');
  const [erro, setErro] = useState<string | null>(null);

  const confirmar = (): void => {
    const instante = new Date(`${data}T${hora}:00`);
    if (Number.isNaN(instante.getTime())) {
      setErro('Data ou hora inválida.');
      return;
    }
    if (instante.getTime() <= Date.now()) {
      setErro('Escolha um horário no futuro.');
      return;
    }
    if (hora < janelaInicio || hora > janelaFim) {
      setErro(`Fora da janela de horário do envio (${janelaInicio} às ${janelaFim}). Ajuste em "Configurações avançadas" ou escolha outra hora.`);
      return;
    }
    setErro(null);
    onConfirm(instante.toISOString());
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Agendar envio"
      description="O envio começa no horário escolhido, no fuso da empresa, e respeita o ritmo e a janela configurados."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="calendario" onClick={confirmar}>
            Confirmar agendamento
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Data" required error={erro}>
          {({ id, describedBy, invalid }) => (
            <Input id={id} aria-describedby={describedBy} aria-invalid={invalid || undefined} type="date" min={toDateOnly(new Date())} value={data} onChange={(e) => setData(e.target.value)} />
          )}
        </Field>
        <Field label="Hora" required hint={`Janela do envio: ${janelaInicio} às ${janelaFim}.`}>
          {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} type="time" value={hora} onChange={(e) => setHora(e.target.value)} />}
        </Field>
      </div>
    </Modal>
  );
}
